import React, { useState, useEffect } from "react";
import {
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  StyledDialog,
  StyledDialogTitle,
} from "../../../components/StyledComponents/Dialog/Dialog.js";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import useMinerPageContext from "../../context/useMinerPageContext.js";
import { useQuery } from "@tanstack/react-query";
import {
  PostJobDetailsByIDCount,
  PostJobInfoByID,
} from "../../../api/api.js";
import { useAuth0 } from "@auth0/auth0-react";
import {
  H6HeadlineSmall,
  H8TitleMedium,
  H9TitleSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import { useHistory } from "react-router-dom/cjs/react-router-dom.js";

const steps = [
  "Validating request",
  "Creating a job",
  "Sending the command to the miners",
];

function LoadingStepIcon(props) {
  console.log("LoadingStepIcon props:", props);
  const { active, completed, icon } = props;
  const { colors } = useTheme();
  if (active) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <CircularProgress
          size={24}
          sx={{ color: colors.primary.main }} 
        />
      </Box>
    );
  }
  if (completed) {
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          bgcolor: colors.success.main,
          color: colors.success.contrastText,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "bold",
        }}
      >
        <span
          className="material-symbols-outlined"
          style={{ fontSize: 16 }}
        >
          check
        </span>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        bgcolor: colors.secondary.light,
        color: colors.secondary.contrastText,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
      }}
    >
      {icon}
    </Box>
  );
}

export default function JobCompletionStepper() {
  const [activeStep, setActiveStep] = useState(0);
  const { jobId, stepperOpen, setStepperOpen } =
    useMinerPageContext();
  const { colors } = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const history = useHistory();
  useEffect(() => {
    let timer;
    if (stepperOpen && activeStep < steps.length - 1) {
      timer = setTimeout(() => {
        setActiveStep((prev) => prev + 1);
      }, 1000); // 2 seconds per step
    }
    return () => clearTimeout(timer);
  }, [activeStep, stepperOpen]);

  const handleClose = () => {
    setStepperOpen(false);
    setActiveStep(0); // Reset progress when closing
  };
  console.log(activeStep, "activeStep");
  let countsPollCount = 0; // Initialize poll count for countsByJobID
  const { data, isFetched: isCountsByJobIDFetched } =
    useQuery({
      queryKey: ["countByJobID", jobId],
      queryFn: async () => {
        if (!jobId) return null;
        const token = await getAccessTokenSilently(); // If jobId is not available, return null
        return PostJobDetailsByIDCount({
          token,
          body: {
            jobid: jobId,
          },
        });
      },
      enabled:
        !!stepperOpen &&
        activeStep === steps.length - 1 &&
        !!jobId,
      refetchInterval: (data, query) => {
        countsPollCount++;
        if (countsPollCount > 3) {
          setActiveStep((prev) => prev + 1); // Move to next step after 3 attempts
          return false; // Stop polling after 3 attempts
        }
        return 1000;
      }, // Pol
      // Only run if stepper is open and jobId is available
    });
  let pollCount = 0;
  const {
    data: infoData,
    isFetched: isFetchedInfo,
  } = useQuery({
    queryKey: ["JobsInfo", jobId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return PostJobInfoByID({
        token,
        body: {
          jobid: jobId,
        },
      });
    },
    enabled:
      !!stepperOpen &&
      activeStep === steps.length - 1 &&
      !!jobId &&
      isCountsByJobIDFetched,
    refetchInterval: (data, query) => {
      pollCount++;
      if (pollCount > 3) {
        setActiveStep((prev) => prev + 1); // Move to next step after 3 attempts
        return false; // Stop polling after 3 attempts
      }
      return 1000;
    }, // Poll every 1 second
  });

  let infoJobData = [];
  if (infoData && infoData.msg) {
    infoJobData =
      isFetchedInfo && infoData.msg ? infoData.msg : [];
  }
  console.log("infoJobData", infoJobData);

  console.log(
    "data stepper",
    !!stepperOpen &&
      activeStep === steps.length - 1 &&
      !!jobId
  );

  return (
    <Box>
      <StyledDialog
        colors={colors}
        open={stepperOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        fullWidth
        maxWidth="sm"
        hideBackdrop
      >
        {/* <StyledDialogTitle>Job Progress</StyledDialogTitle> */}
        <StyledDialogTitle>
          <H6HeadlineSmall style={{ textAlign: "left" }}>
            We are processing your request
          </H6HeadlineSmall>
        </StyledDialogTitle>
        <DialogContent>
          <Stepper
            orientation="vertical"
            activeStep={activeStep}
            sx={{
              gap: 4,
            }}
            // alternativeLabel
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel
                sx={{
                  "& .MuiStepLabel-label": {
                    color: colors.text.primary, 
                  },
                  "& .MuiStepLabel-label.Mui-active": {
                    color: colors.primary.main, 
                  },
                  "& .MuiStepLabel-label.Mui-completed": {
                    color: colors.success?.main, 
                  },
                  "& .MuiStepLabel-label.Mui-disabled": {
                    color: colors.text.muted, 
                  },
                }}
                  StepIconComponent={LoadingStepIcon}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          {activeStep === steps.length - 1 &&
            isCountsByJobIDFetched &&
            data && (
              // <Box
              //   sx={{
              //     display: "grid",
              //     gridTemplateColumns: "auto auto",
              //     backgroundColor: colors.background.accent,
              //     padding: 8,
              //     borderRadius: "1rem",
              //   }}
              // >
              //   <>
              //     <BodyMedium>
              //       Total number of miners this job has been
              //       submitted to :
              //     </BodyMedium>
              //     <BodyMedium>{data.count || 0}</BodyMedium>
              //   </>
              //   <>
              //     <BodyMedium>
              //       No of connected miners:
              //     </BodyMedium>
              //     <BodyMedium>
              //       {infoJobData?.serialsActiveCount || 0}
              //     </BodyMedium>
              //   </>
              // </Box>
              <></>
            )}

          {activeStep === steps.length && (
            <Box
              display={"flex"}
              flexDirection="column"
              alignItems="flex-start"
              gap={4}
              backgroundColor={colors.background.accent}
              padding={4}
              borderRadius="1rem"
            >
              <H8TitleMedium>
                🎉 All steps completed!
              </H8TitleMedium>
              <H9TitleSmall>
                {`A job has been created with ID: ${jobId}. Please click on the button to take you to the job details page.`}
              </H9TitleSmall>
              <Box
                display="flex"
                flexDirection={"row"}
                justifyContent="flex-end"
                flexGrow={1}
                width="100%"
                gap={2}
              >
                <SecondaryButton colors={colors} onClick={handleClose}>
                  Close
                </SecondaryButton>
                <PrimaryButton colors={colors}
                  onClick={() => {
                    history.push(
                      `/jobDetails/?jobID=${jobId}`
                    );
                    handleClose();
                  }}
                >
                  Go to Job Details
                </PrimaryButton>
              </Box>
            </Box>
          )}
        </DialogContent>
      </StyledDialog>
    </Box>
  );
}
